import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useDepots, fmtDepot } from "@/lib/depots";
import { MagnifyingGlass, Bus, MapPin, UserCircle, IdentificationCard, Handshake, X } from "@phosphor-icons/react";

export default function GlobalSearch() {
  const depotMap = useDepots();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const timer = useRef();
  const wrap = useRef();

  useEffect(()=>{
    if (q.length < 2) { setResults(null); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async ()=>{
      try { const { data } = await api.get(`/search/global?q=${encodeURIComponent(q)}`); setResults(data); }
      catch {}
    }, 250);
  }, [q]);

  useEffect(()=>{
    const onClick = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", onClick);
    return ()=>document.removeEventListener("click", onClick);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };

  const total = results ? Object.values(results).reduce((s,arr)=>s+arr.length,0) : 0;

  return (
    <div ref={wrap} className="relative">
      <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-md w-full max-w-md">
        <MagnifyingGlass size={16} className="text-slate-500 shrink-0"/>
        <input
          data-testid="topbar-search"
          value={q}
          onChange={(e)=>{setQ(e.target.value); setOpen(true);}}
          onFocus={()=>setOpen(true)}
          className="bg-transparent text-sm outline-none flex-1 min-w-0"
          placeholder="Search buses, routes, crew..."/>
        {q && <button onClick={()=>{setQ(""); setResults(null);}}><X size={14} className="text-slate-400"/></button>}
      </div>
      {open && q.length >= 2 && results && (
        <div className="fixed sm:absolute left-3 right-3 sm:left-auto sm:right-0 top-16 sm:top-full sm:mt-2 sm:w-[480px] max-w-[calc(100vw-1.5rem)] max-h-[70vh] overflow-auto bg-white border border-slate-200 rounded-lg shadow-2xl z-[1500] scroll-thin">
          <div className="sticky top-0 bg-slate-50 border-b border-slate-200 px-3 py-2 text-xs text-slate-600 flex justify-between gap-2">
            <span className="truncate">{total === 0 ? "No matches" : `${total} result${total>1?"s":""} for "${q}"`}</span>
            <span className="font-mono text-slate-400 whitespace-nowrap hidden sm:inline">⏎ to open</span>
          </div>
          {[
            { k:"buses", l:"Buses", path:"/fleet", icon:Bus, render:(b)=>(<>
              <div className="font-mono font-bold text-[#1E3A8A]">{b.fleet_id}</div>
              <div className="text-[11px] text-slate-600">{b.registration_number} · {b.bus_type} · {fmtDepot(b.depot, depotMap)} · {b.status}</div></>) },
            { k:"routes", l:"Routes", path:"/schedule", icon:MapPin, render:(r)=>(<>
              <div className="font-mono font-bold text-[#1E3A8A]">{r.route_no || r.route_id}</div>
              <div className="text-[11px] text-slate-600 break-words">{r.name || r.route_id}{r.depot ? <> · {fmtDepot(r.depot, depotMap)}</> : null}{r.bus_type ? <> · {r.bus_type}</> : null}</div></>) },
            { k:"drivers", l:"Drivers", path:"/crew", icon:UserCircle, render:(d)=>(<>
              <div className="font-semibold">{d.name}</div>
              <div className="text-[11px] text-slate-600">{d.employee_id} · {fmtDepot(d.depot, depotMap)} · {d.phone}</div></>) },
            { k:"conductors", l:"Conductors", path:"/crew", icon:IdentificationCard, render:(c)=>(<>
              <div className="font-semibold">{c.name}</div>
              <div className="text-[11px] text-slate-600">{c.employee_id} · {fmtDepot(c.depot, depotMap)} · {c.phone}</div></>) },
            { k:"operators", l:"Operators", path:"/operators", icon:Handshake, render:(o)=>(<>
              <div className="font-semibold">{o.name}</div>
              <div className="text-[11px] text-slate-600">{o.operator_id} · {o.gst}</div></>) },
            { k:"stops", l:"Bus Stops", path:"/bus-stops", icon:MapPin, render:(s)=>(<>
              <div className="font-semibold">{s.bus_stop_name}</div>
              <div className="text-[11px] text-slate-600">#{s.bus_stop_id} · {s.bus_stop_code}</div></>) },
          ].map(g => results[g.k]?.length > 0 && (
            <div key={g.k}>
              <div className="bg-slate-50 px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold text-slate-500 flex items-center gap-1.5 border-b border-slate-100">
                <g.icon size={12}/>{g.l} ({results[g.k].length})
              </div>
              {results[g.k].map((item, i) => (
                <button key={i} onClick={()=>go(g.path)} className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors border-b border-slate-50 last:border-0">
                  {g.render(item)}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
